import { AlertCircle, Users, Sparkles, Loader2, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useDesign } from '@/contexts/DesignContext';

/**
 * UpgradeRequiredDialog — Global reusable upgrade prompt.
 * 
 * Shows when a user hits their plan's team member limit.
 * Displays the next tier's details and lets the user upgrade or decline.
 *
 * Props:
 *  - open: boolean — controls dialog visibility
 *  - onOpenChange: (open: boolean) => void
 *  - upgradeInfo: { next_tier_name, next_tier_users, next_tier_ai_credits, next_tier_price } | null
 *  - upgrading: boolean — shows loading state on upgrade button
 *  - onUpgrade: () => void — called when user clicks "Upgrade Now"
 *  - onDecline: () => void — called when user clicks "No Thanks"
 */
export function UpgradeRequiredDialog({ open, onOpenChange, upgradeInfo, upgrading = false, onUpgrade, onDecline }) {
  const { isStudio } = useDesign();

  /* Studio (mocks/settings-b.md frame 8): the sheet's "card inside a confirm"
     — the tier card with the fg ring, no ×, No thanks + one primary. Same
     props, same handlers; classic falls through untouched. */
  if (isStudio) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-ui-dialog="sbconfirm" data-testid="upgrade-required-dialog">
          <DialogHeader>
            <DialogTitle>Out of team member slots</DialogTitle>
            <DialogDescription className="st-tbds">
              You are out of team member slots. You can cancel a pending invite to free up a slot, or upgrade your plan.
            </DialogDescription>
          </DialogHeader>

          {upgradeInfo && (
            <div className="st-tbplan st-tbon">
              <div>
                <div className="st-tbpn">{upgradeInfo.next_tier_name}</div>
                <div className="st-tbpp"><b>${upgradeInfo.next_tier_price}</b><span>/mo</span></div>
              </div>
              <ul>
                <li><Users />{upgradeInfo.next_tier_users} team members</li>
                <li><Sparkles />{upgradeInfo.next_tier_ai_credits} AI chats a month</li>
                <li><Shield />3 admin seats</li>
              </ul>
            </div>
          )}

          <DialogFooter className="st-tbfoot">
            <button type="button" className="st-btn st-ghost" onClick={onDecline} data-testid="upgrade-decline-btn">
              No thanks
            </button>
            <button type="button" className="st-btn st-pri" onClick={onUpgrade} disabled={upgrading} data-testid="upgrade-confirm-btn">
              {upgrading ? (<><Loader2 className="animate-spin" />Upgrading…</>) : `Upgrade now · $${upgradeInfo?.next_tier_price}/mo`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="upgrade-required-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertCircle className="w-5 h-5" />
            Out of Team Member Slots
          </DialogTitle>
          <DialogDescription>
            You are out of team member slots. You can cancel a pending invite to free up a slot, or upgrade your plan.
          </DialogDescription>
        </DialogHeader>
        
        {upgradeInfo && (
          <div className="py-4">
            <div className="bg-gradient-to-r from-indigo-50 dark:from-indigo-950/40 to-purple-50 rounded-xl p-4 border border-indigo-100">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Upgrade to {upgradeInfo.next_tier_name}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <Users className="w-4 h-4 text-indigo-500" />
                  <span>{upgradeInfo.next_tier_users} team members</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <Sparkles className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                  <span>{upgradeInfo.next_tier_ai_credits} AI chats/month</span>
                </div>
                <div className="mt-3 pt-3 border-t border-indigo-100">
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    ${upgradeInfo.next_tier_price}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">/month</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onDecline}
            className="w-full sm:w-auto"
            data-testid="upgrade-decline-btn"
          >
            No Thanks
          </Button>
          <Button 
            onClick={onUpgrade}
            disabled={upgrading}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700"
            data-testid="upgrade-confirm-btn"
          >
            {upgrading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upgrading...
              </>
            ) : (
              `Upgrade Now - $${upgradeInfo?.next_tier_price}/mo`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
